import { useState, useEffect } from 'react';
import { Card, Input, Button, Select, Form, message } from 'antd';
import { FiMail, FiServer, FiLock, FiCheckCircle, FiInfo, FiLayers, FiUser, FiSend } from 'react-icons/fi';
import { apiFetch } from '../api';

const SystemSettings = () => {
  const [form] = Form.useForm();
  const [provider, setProvider] = useState('smtp');
  const [status, setStatus] = useState({ connected: false, checked: false, message: '' });
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const fetchSmtpConfig = async () => {
    try {
      const response = await apiFetch('/api/system-settings/smtp');
      const payload = await response.json();
      const statusResponse = await apiFetch('/api/system-settings/smtp/status');
      const statusPayload = await statusResponse.json();

      if (response.ok && payload.success && payload.data) {
        const config = payload.data;

        form.setFieldsValue({
          host: config.host,
          port: config.port,
          securityProtocol: config.securityProtocol || 'SSL/TLS',
          fromName: config.fromName,
          fromEmail: config.fromEmail,
          authPassword: config.authPassword
        });
      }

      if (statusResponse.ok && statusPayload.success && statusPayload.data.configured) {
        setStatus({
          connected: true,
          checked: true,
          message: 'Email provider active (configured for your company).'
        });
      } else {
        setStatus({ connected: false, checked: true, message: 'No email provider configured for your company yet.' });
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      message.error('Failed to load email configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSmtpConfig();
  }, []);

  const handleTestAndSave = async (values) => {
    setTesting(true);
    setStatus({ connected: false, checked: false, message: '' });
    try {
      const payload = { ...values, provider: 'smtp' };
      const response = await apiFetch('/api/system-settings/smtp', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (response.ok && result.success) {
        message.success(result.message || 'Configuration saved successfully.');
        setStatus({
          connected: true,
          checked: true,
          message: 'SMTP connection successfully verified and active.'
        });
        fetchSmtpConfig();
      } else {
        setStatus({
          connected: false,
          checked: true,
          message: result.message || 'Verification failed.'
        });
        message.error(result.message || 'Failed to verify settings.');
      }
    } catch (error) {
      console.error('Test error:', error);
      setStatus({ connected: false, checked: true, message: 'Connection timeout or network failure.' });
      message.error('Testing failed.');
    } finally {
      setTesting(false);
    }
  };

  const handleResetToDefault = async () => {
    try {
      const response = await apiFetch('/api/system-settings/smtp', {
        method: 'DELETE'
      });
      const data = await response.json();
      if (response.ok && data.success) {
        form.resetFields();
        setStatus({ connected: false, checked: true, message: 'Settings reset and removed from system.' });
        message.success('SMTP configuration reset successfully.');
      } else {
        message.error(data.message || 'Failed to reset settings.');
      }
    } catch (error) {
      console.error('Reset error:', error);
      message.error('Failed to reset configuration.');
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      const response = await apiFetch('/api/system-settings/smtp/test-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: form.getFieldValue('fromEmail') })
      });
      const payload = await response.json();
      if (response.ok && payload.success) {
        message.success(payload.message || 'Test email sent! Check your inbox.');
      } else {
        message.error(payload.message || 'Failed to send test email.');
      }
    } catch (error) {
      console.error('Test email error:', error);
      message.error('Failed to send test email.');
    } finally {
      setSendingTest(false);
    }
  };

  const handleProviderSwitch = (newProvider) => {
    form.resetFields();
    setProvider(newProvider);
  };

  if (loading) {
    return (
      <div className="wm-page flex items-center justify-center min-h-[300px]">
        <div className="text-slate-500 font-medium text-base">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="wm-page space-y-6 max-w-7xl mx-auto">

      {/* Page Header */}
      <div className="wm-card-header wm-card-flat dark:bg-vms-navy dark:border-white/10">
        <div>
          <h1 className="wm-page-title text-xl dark:text-white">System Settings</h1>
          <p className="wm-page-subtitle">Configure email servers, system triggers, and mailing preferences.</p>
        </div>
      </div>

      {/* ── SMTP Configuration Form ──────────────────────────────────────── */}
      <Card className="wm-card-flat dark:bg-vms-navy dark:border-white/10" title="SMTP Configuration">
          <Form form={form} layout="vertical" onFinish={handleTestAndSave} className="space-y-4" requiredMark={false}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Form.Item
                name="host"
                label={<span className="font-semibold text-slate-700 dark:text-slate-200">SMTP Host <span className="text-rose-500">*</span></span>}
                rules={[{ required: true, message: 'SMTP host is required' }]}
              >
                <Input size="large" prefix={<FiServer />} placeholder="smtp.gmail.com" className="h-11 rounded-lg border-slate-200" />
              </Form.Item>

              <Form.Item
                name="port"
                label={<span className="font-semibold text-slate-700 dark:text-slate-200">Port <span className="text-rose-500">*</span></span>}
                rules={[{ required: true, message: 'Port is required' }]}
              >
                <Input size="large" placeholder="465" className="h-11 rounded-lg border-slate-200" />
              </Form.Item>

              <Form.Item
                name="securityProtocol"
                label={<span className="font-semibold text-slate-700 dark:text-slate-200">Security Protocol <span className="text-rose-500">*</span></span>}
                initialValue="SSL/TLS"
              >
                <Select size="large" className="h-11 border-slate-200" options={[
                  { value: 'SSL/TLS', label: 'SSL/TLS (Recommended)' },
                  { value: 'None', label: 'None' }
                ]} />
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Form.Item
                name="fromName"
                label={<span className="font-semibold text-slate-700 dark:text-slate-200">From Name</span>}
              >
                <Input size="large" prefix={<FiUser />} placeholder="Sender Name" className="h-11 rounded-lg border-slate-200" />
              </Form.Item>

              <Form.Item
                name="fromEmail"
                label={<span className="font-semibold text-slate-700 dark:text-slate-200">Email Address <span className="text-rose-500">*</span></span>}
                rules={[
                  { required: true, message: 'From email is required' },
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <Input size="large" prefix={<FiMail />} placeholder="sender@company.com" className="h-11 rounded-lg border-slate-200" />
              </Form.Item>

              <Form.Item
                name="authPassword"
                label={<span className="font-semibold text-slate-700 dark:text-slate-200">App Password <span className="text-rose-500">*</span></span>}
                rules={[{ required: true, message: 'SMTP Auth Password is required' }]}
              >
                <Input.Password size="large" prefix={<FiLock />} placeholder="••••••••••••" className="h-11 rounded-lg border-slate-200" />
              </Form.Item>
            </div>

            {/* SMTP Tips */}
            <div className="bg-[#f0f7fc] dark:bg-slate-900/40 rounded-xl p-5 border border-sky-100/50 dark:border-white/5 flex gap-3 text-xs text-slate-600 dark:text-slate-300">
              <FiInfo className="text-sky-600 dark:text-sky-400 text-lg flex-shrink-0 mt-0.5" />
              <div className="space-y-1.5">
                <strong className="text-slate-800 dark:text-white text-sm">Configuration Tips</strong>
                <p>Use an <strong>App Password</strong> or API key depending on your SMTP provider.</p>
                <p>Common SMTP port is <code>465</code> (SSL/TLS) or <code>587</code> (STARTTLS).</p>
                <p><strong>☁️ Hosting on Render (Free Tier)?</strong> Render blocks outbound SMTP ports 25, 465, and 587. To send emails from Render, you can use an SMTP relay provider (such as SendGrid, Mailgun, or Postmark) on port <strong>2525</strong> or <strong>8025</strong>, which are not blocked.</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center pt-4 border-t border-slate-100 dark:border-white/5">
              <Button size="large" onClick={handleResetToDefault} className="rounded-lg text-slate-600 border-slate-200 hover:bg-slate-50 font-medium">
                Reset to Default
              </Button>
              <div className="flex gap-3">
                <Button
                  size="large" icon={<FiSend />} loading={sendingTest} disabled={!status.connected}
                  onClick={handleSendTestEmail}
                  className="rounded-lg font-medium border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  {sendingTest ? 'Sending...' : 'Send Test Email'}
                </Button>
                <Button type="primary" htmlType="submit" loading={testing} size="large" className="wm-btn-primary px-8 rounded-lg font-semibold">
                  {testing ? 'Testing Connection...' : 'Test & Save Configuration'}
                </Button>
              </div>
            </div>
          </Form>
        </Card>

    </div>
  );
};

export default SystemSettings;
